import { UserOrdersHubApi } from '@coretava/api/ecommerce-service';
import { BirthdayGiftApi, BirthdayGiftDto } from '@coretava/api/integrations-service';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { isDateSameOrAfter, isDateSameOrBefore } from '@gamiphy/utils/date';
import { addDays, subMonths } from 'date-fns';
import {
  action, computed, makeAutoObservable, observable, runInAction, when,
} from 'mobx';

class BirthdayGiftStore {
  @observable initialized: boolean = false;

  @observable birthdayGift?: BirthdayGiftDto = undefined;

  @observable purchasesWithinPeriod?: number = undefined;

  constructor() {
    makeAutoObservable(this);

    void this.initialize();
  }

  @computed
  get birthdayGiftApi(): BirthdayGiftApi {
    return new BirthdayGiftApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: UserStore.token,
    });
  }

  @computed
  get userOrdersHubApi(): UserOrdersHubApi {
    return new UserOrdersHubApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: UserStore.token,
    });
  }

  @action
  initialize(): void {
    void this.fetchBirthdayGift();
    void this.fetchTotalPurchases();
  }

  @action
  async fetchTotalPurchases(): Promise<void> {
    await when(() => !!this.birthdayGift);

    if (UserStore.currentYearBirthDate && this.birthdayGift) {
      const startDate = subMonths(UserStore.currentYearBirthDate, this.birthdayGift?.trackingPeriod);
      const response = await this.userOrdersHubApi.userOrdersControllerGetTotalByUser(
        AppStore.app.id,
        startDate.toISOString(),
        UserStore.currentYearBirthDate?.toISOString(),
      );

      runInAction(() => {
        this.purchasesWithinPeriod = response.data.total;
      });
    }
  }

  @action
  async fetchBirthdayGift(): Promise<void> {
    await when(() => AppStore.initialized && UserStore.isCooperado);

    const response = await this.birthdayGiftApi.birthdayGiftsControllerGetByApp(AppStore.app.id);

    runInAction(() => {
      this.birthdayGift = response.data.birthdayGift ?? undefined;
    });
  }

  @computed get progress(): number {
    return ((this.purchasesWithinPeriod ?? 0) / (this.birthdayGift?.minPurchaseValue || 1)) * 100;
  }

  @computed get isGoalAchieved(): boolean {
    return this.progress >= 100;
  }

  @computed get isWithinExpiryPeriod(): boolean {
    return !!UserStore.currentYearBirthDate &&
     !!this.birthdayGift &&
     isDateSameOrAfter(new Date(), UserStore.currentYearBirthDate) &&
     isDateSameOrBefore(new Date(), addDays(UserStore.currentYearBirthDate, this.birthdayGift.expiry));
  }

  @computed get isWithinTrackingPeriod(): boolean {
    return !!UserStore.currentYearBirthDate &&
     !!this.birthdayGift &&
     isDateSameOrBefore(new Date(), UserStore.currentYearBirthDate) &&
     isDateSameOrAfter(new Date(), subMonths(UserStore.currentYearBirthDate, this.birthdayGift?.trackingPeriod));
  }

  @computed get showBirthdayGift(): boolean {
    return !!this.birthdayGift?.enabled && (this.isWithinTrackingPeriod || (this.isWithinExpiryPeriod && this.isGoalAchieved));
  }
}

export default new BirthdayGiftStore();
